import React from 'react';

import {
  ClientContextType,
  ClientProviderType,
  ProviderStateType,
  TokenStoreType,
} from './types';
import {
  DemandRegisterBodyType,
  RegisterBodyType,
  ForgotPasswordType,
  ResetPasswordType,
  KanbanColumnType,
  UpdateKanbanColumnType,
} from '../../types';
import { message } from 'antd';

const GET_CACHE_TTL_MS = 30_000;

const isDedupedGet = (
  path: string,
  options?: { toBlob?: boolean; isChatServer?: boolean }
) => !options?.toBlob && !options?.isChatServer && path.startsWith('/me/groups');

const defaultContext: ClientContextType = {
  status: 'anonymous',
  hasError: false,
  login: () => {},
  autoConnect: () => {},
  logout: () => {},
  getAsAnonUser: () => {},
  putAsAnonUser: () => {},
  get: () => () => {},
  getPromise: () => {},
  post: () => () => {},
  postJson: () => () => {},
  postMessaging: () => () => {},
  postPromise: () => {},
  put: () => () => {},
  patch: () => () => {},
  deletion: () => () => {},
  demandRegister: () => {},
  register: () => {},
  upload: () => {},
  getToken: () => {
    return {
      accessToken: 'string',
      refreshToken: 'string',
      expiredAt: new Date(),
    };
  },
  forgotPassword: () => {},
  resetPassword: () => {},
  addKanbanColumnReq: () => {},
  updateKanbanColumnReq: () => {},
  updateKanbanColumnsReq: () => {},
};

export const ClientContext =
  React.createContext<ClientContextType>(defaultContext);

export const makeClientProvider =
  (tokenStore: TokenStoreType): React.FC<ClientProviderType> =>
  ({ client, children }) => {
    const [status, setStatus] = React.useState<ProviderStateType>({
      status: 'loading',
    });

    // Dédoublonnage des GET /me/groups* : plusieurs composants d'une même page
    // (sélecteurs de cible, filtres, tableau de bord) demandent la même liste au
    // même moment. Une réponse est partagée par les appels en cours et réutilisée
    // 30 s ; toute écriture passée par ce client vide ce cache.
    const getCache = React.useRef(
      new Map<string, { at: number; promise: Promise<any> }>()
    );

    const api: Omit<ClientContextType, 'status'> = React.useMemo(() => {
      const login = async (
        email: string,
        password: string,
        successCallback?: () => void,
        failureCallback?: () => void
      ) => {
        try {
          setStatus({ status: 'authenticating' });
          const token = await client.auth.userAuthentication({
            email,
            password,
          });

          tokenStore.set(token);
          setStatus({ status: 'authenticated' });
          successCallback && successCallback();
        } catch (err) {
          setStatus({ status: 'anonymous', hasError: true });
          //TODO: expose error to application
          console.warn(err);
          failureCallback && failureCallback();
        }
      };

      const autoConnect = async (
        tokenParam: string,
        successCallback: () => void,
        failureCallback: (error?: any) => void
      ) => {
        try {
          const token = await client.auth.userAuthentication({
            service: 'auto-connect',
            token: tokenParam,
          });
          tokenStore.set(token);
          setStatus({ status: 'authenticated' });
          successCallback && successCallback();
        } catch (error) {
          setStatus({ status: 'anonymous', hasError: true });
          failureCallback && failureCallback(error);
        }
      };

      const demandRegister = async (
        body: DemandRegisterBodyType,
        successCallback: () => void,
        failureCallback: () => void
      ) => {
        await client.auth.applicationAuthentication();

        post(
          '/notifications/customer/create',
          body,
          successCallback,
          failureCallback
        );
      };

      const register = async (
        body: RegisterBodyType,
        successCallback: (customerId: string) => void,
        failureCallback: () => void
      ) => {
        await client.auth.applicationAuthentication();

        post('/customers/registration', body, successCallback, failureCallback);
      };

      const forgotPassword = async (
        body: ForgotPasswordType,
        successCallback: () => void,
        failureCallback: () => void
      ) => {
        await client.auth.applicationAuthentication();

        post(
          '/forgot-password/request',
          body,
          successCallback,
          failureCallback
        );
      };

      const resetPassword = async (
        token: string,
        body: ResetPasswordType,
        successCallback: () => void,
        failureCallback: () => void
      ) => {
        await client.auth.applicationAuthentication();

        post(
          `/forgot-password/reset/${token}`,
          body,
          successCallback,
          failureCallback
        );
      };

      const addKanbanColumnReq = async (
        body: KanbanColumnType,
        successCallback: () => void,
        failureCallback: () => void
      ) => {
        post(
          '/customers/kanban/columns',
          body,
          successCallback,
          failureCallback
        );
      };

      const updateKanbanColumnReq = async (
        body: UpdateKanbanColumnType,
        successCallback: () => void,
        failureCallback: () => void
      ) => {
        put(
          `/customers/kanban/columns/${body.id}`,
          body.data,
          successCallback,
          failureCallback
        );
      };

      const updateKanbanColumnsReq = async (
        bodyList: UpdateKanbanColumnType[],
        successCallback: () => void,
        failureCallback: () => void
      ) => {
        const promises = bodyList.map((body: UpdateKanbanColumnType) =>
          updateKanbanColumnReq(
            body,
            () =>
              message.success(
                `La colonne ${body.data.name} a été bien déplacé`
              ),
            () =>
              message.error(
                `Une erreur est survenue lors du déplacement de la colonne ${body.data.name}`
              )
          )
        );
        Promise.allSettled(promises).then(results =>
          results.find(result => result.status === 'rejected')
            ? failureCallback()
            : successCallback()
        );
      };

      const logout = () => {
        tokenStore.clear();
        client.auth.logout();
        setStatus({ status: 'anonymous', hasError: false });
      };

      // Déconnecte automatiquement le user si l'API renvoie 401 sur n'importe quel call.
      // Couvre le cas où un access token devient invalide (ex: rotation des clés RSA côté
      // serveur OAuth). Retourne true si un 401 a été traité, pour court-circuiter la suite.
      const handleUnauthorized = (response: Response): boolean => {
        if (response.status === 401) {
          logout();
          return true;
        }
        return false;
      };

      const getAsAnonUser = async (
        path: string,
        callback: (ret: any) => void,
        failureCallback?: () => void
      ) => {
        await client.auth.applicationAuthentication();

        get(path, callback, failureCallback);
      };

      const putAsAnonUser = async (
        path: string,
        data: any,
        successCallback: () => void,
        failureCallback?: () => void
      ) => {
        await client.auth.applicationAuthentication();

        put(path, data, successCallback, failureCallback);
      };

      const get = (
        path: string,
        successCallback: (ret: any) => void,
        failureCallback?: () => void,
        options?: {
          toBlob?: boolean;
          isChatServer?: boolean;
        }
      ) => {
        let active = true;
        const request = () =>
          client
            .request(path, {
              method: 'GET',
              baseUrl: options?.isChatServer
                ? process.env.REACT_APP_MR_CLIENT_CHAT_URL
                : undefined,
            })
            .then(payload => {
              if (handleUnauthorized(payload)) return;
              if (payload.status >= 200 && payload.status <= 206) {
                return options?.toBlob ? payload.blob() : payload.json();
              } else {
                throw options?.toBlob ? payload.blob() : payload.json();
              }
            });

        let promise: Promise<any>;
        if (isDedupedGet(path, options)) {
          const cached = getCache.current.get(path);
          if (cached && Date.now() - cached.at < GET_CACHE_TTL_MS) {
            promise = cached.promise;
          } else {
            promise = request();
            getCache.current.set(path, { at: Date.now(), promise });
            promise.catch(() => getCache.current.delete(path));
          }
        } else {
          promise = request();
        }

        promise
          .then(ret => {
            if (active && ret)
              ret.errors
                ? failureCallback && failureCallback()
                : successCallback(ret);
          })
          .catch(() => active && failureCallback && failureCallback());
        return () => {
          active = false;
        };
      };

      const getPromise = (path: string, toJson = true) =>
        client.request(path, { method: 'GET' }).then(res => {
          if (handleUnauthorized(res)) return Promise.reject();
          return toJson ? res.json() : res.blob();
        });

      const post = (
        path: string,
        data: any,
        successCallback: ((createdId: string) => void) | (() => void),
        failureCallback?: (res: any) => void
      ) => {
        getCache.current.clear();
        let active = true;
        client.request(path, { method: 'POST', body: data }).then(res => {
          getCache.current.clear();
          if (handleUnauthorized(res)) return;
          if (
            active &&
            (res.status === 201 || res.status === 204 || res.status === 202)
          ) {
            /* Get ID of the created resource if it exists */
            const createdId = res.headers.get('Location')?.split('/')?.pop()!;
            return successCallback(createdId);
          } else {
            return failureCallback && failureCallback(res);
          }
        });
        return () => {
          active = false;
        };
      };

      /* POST dont la réponse (200) porte un corps JSON, rendu à l'appelant */
      const postJson = (
        path: string,
        data: any,
        successCallback: (response: any) => void,
        failureCallback?: () => void
      ) => {
        getCache.current.clear();
        let active = true;
        client
          .request(path, { method: 'POST', body: data })
          .then(res => {
            getCache.current.clear();
            if (handleUnauthorized(res)) return;
            if (res.status !== 200) throw res;
            return res.json();
          })
          .then(json => {
            if (active && json) successCallback(json);
          })
          .catch(() => active && failureCallback && failureCallback());
        return () => {
          active = false;
        };
      };

      const postMessaging = (
        path: string,
        data: any,
        successCallback: ((response: any) => void) | (() => void),
        failureCallback?: () => void
      ) => {
        let active = true;
        client
          .request(path, {
            method: 'POST',
            baseUrl: process.env.REACT_APP_MR_CLIENT_CHAT_URL,
            body: data,
          })
          .then(payload => {
            if (handleUnauthorized(payload)) return Promise.reject();
            return payload.json();
          })
          .then(json => {
            if (active && json) {
              json.errors
                ? failureCallback && failureCallback()
                : successCallback(json);
            }
          })
          .catch(() => failureCallback && failureCallback());
        return () => {
          active = false;
        };
      };

      const postPromise = (path: string, data: any) =>
        client.request(path, { method: 'POST', body: data }).then(res => {
          if (handleUnauthorized(res)) return Promise.reject();
          if (res.status === 201 || res.status === 204 || res.status === 202) {
            /* Get ID of the created resource if it exists */
            const createdId = res.headers.get('Location')?.split('/')?.pop()!;
            return createdId;
          } else {
            throw new Error();
          }
        });

      const put = (
        path: string,
        data: any,
        successCallback: (response?: any) => void,
        failureCallback?: () => void,
        options?: {
          isChatServer?: boolean;
        }
      ) => {
        getCache.current.clear();
        let active = true;
        client
          .request(path, {
            method: 'PUT',
            baseUrl: options?.isChatServer
              ? process.env.REACT_APP_MR_CLIENT_CHAT_URL
              : undefined,
            body: data,
          })
          .then(res => {
            getCache.current.clear();
            if (handleUnauthorized(res)) return;
            return active && (res.status === 202 || res.status === 201)
              ? successCallback()
              : failureCallback && failureCallback();
          })
          .catch(() => {
            failureCallback && failureCallback();
          });
        return () => {
          active = false;
        };
      };

      const patch = (
        path: string,
        data: any,
        successCallback: () => void,
        failureCallback?: (res: any) => void,
        options?: {
          isChatServer?: boolean;
        }
      ) => {
        getCache.current.clear();
        let active = true;
        client
          .request(path, {
            method: 'PATCH',
            baseUrl: options?.isChatServer
              ? process.env.REACT_APP_MR_CLIENT_CHAT_URL
              : undefined,
            body: data,
          })
          .then(res => {
            getCache.current.clear();
            if (handleUnauthorized(res)) return;
            return active && res.status === 202
              ? successCallback()
              : failureCallback && failureCallback(res);
          });
        return () => {
          active = false;
        };
      };

      const deletion = (
        path: string,
        data: any,
        successCallback: () => void,
        failureCallback?: () => void,
        options?: {
          isChatServer?: boolean;
        }
      ) => {
        getCache.current.clear();
        let active = true;
        client
          .request(path, {
            method: 'DELETE',
            baseUrl: options?.isChatServer
              ? process.env.REACT_APP_MR_CLIENT_CHAT_URL
              : undefined,
            body: data,
          })
          .then(res => {
            getCache.current.clear();
            if (handleUnauthorized(res)) return;
            if (
              active &&
              (res.status === 200 ||
                res.status === 201 ||
                res.status === 202 ||
                res.status === 204)
            ) {
              return successCallback();
            }
            return failureCallback && failureCallback();
          })
          .catch(() => failureCallback && failureCallback());
        return () => {
          active = false;
        };
      };

      const upload = (
        body: any, // Any instead of Blob so that we can access the file name
        successCallback: (mediaId: any) => void,
        failureCallback: () => void
      ) => {
        post(
          '/medias',
          body,
          (mediaId: any) => successCallback(mediaId),
          (res: any) => {
            /* Image refusée par la modération : on dit pourquoi */
            if (res?.status === 422) {
              res
                .json()
                .then(
                  (error: any) =>
                    error?.errors?.message &&
                    message.error(error.errors.message, 6)
                )
                .catch(() => {});
            }
            failureCallback();
          }
        );
      };

      const getToken = () => {
        return client.auth.getToken();
      };

      return {
        getToken,
        login,
        autoConnect,
        logout,
        getAsAnonUser,
        putAsAnonUser,
        get,
        getPromise,
        post,
        postJson,
        postMessaging,
        postPromise,
        put,
        patch,
        deletion,
        demandRegister,
        register,
        upload,
        forgotPassword,
        resetPassword,
        addKanbanColumnReq,
        updateKanbanColumnsReq,
        updateKanbanColumnReq,
      };
    }, [client]);

    const logout = () => {
      tokenStore.clear();
      client.auth.logout();
      setStatus({ status: 'anonymous', hasError: false });
    };
    React.useEffect(() => {
      const token = tokenStore.get();

      if (client.auth.getToken()) {
        setStatus({ status: 'authenticated' });
      }

      if (!client.auth.getToken() && token) {
        const _token = {
          accessToken: token.accessToken,
          expiredAt: new Date(token.expiredAt),
          refreshToken: token.refreshToken,
        };
        client.auth.setToken(_token);
        setStatus({ status: 'authenticated' });
      }

      if (!client.auth.getToken() && !token) {
        logout();
      }

      client.event.addListener('post_auth', () => {
        const token = client.auth.getToken();
        if (token.accessToken && token.refreshToken && token.expiredAt) {
          tokenStore.set(token);
          setStatus({ status: 'authenticated' });
        }
      });

      client.event.addListener('fail_auth', () => {
        logout();
      });
      // eslint-disable-next-line
    }, []);

    return (
      <ClientContext.Provider value={{ ...status, ...api }}>
        {children}
      </ClientContext.Provider>
    );
  };

const AUTH_STORAGE_KEY = 'auth';

const tokenStore: TokenStoreType = {
  get: () => {
    const token = localStorage.getItem(AUTH_STORAGE_KEY);

    if (!token) return null;

    try {
      return JSON.parse(token);
    } catch (e) {
      return null;
    }
  },
  set: token => localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(token)),
  clear: () => localStorage.removeItem(AUTH_STORAGE_KEY),
};

export const ClientProvider = makeClientProvider(tokenStore);
ClientProvider.displayName = 'ClientProvider';

export const useClient = () => React.useContext(ClientContext);
